/** Orquestamos dominio e infraestructura para que la UI no toque Supabase ni Dexie directamente. */
import {
  db,
  listarProductosLocalDeProveedor,
  contarProductosDeProveedor,
  blobAArrayBuffer,
  type ProductoPendiente,
  type RutaDeBorrado,
} from '../infrastructure/offline/db'
import { sincronizarPendientes } from '../infrastructure/offline/sincronizador'
import { obtenerPrefijoPersona } from '../infrastructure/offline/preferencias'
import { esErrorDeCuotaLlena, MENSAJE_SIN_ESPACIO } from '../infrastructure/util/almacenamiento'
import { backendDeUrl } from '../infrastructure/storage/rutas-storage'
import { crearCodigoProveedorTemporal } from '../domain/codigo-proveedor'

/**
 * Envolvemos el guardado en Dexie para detectar `QuotaExceededError`
 * especificamente — sin esto, si el dispositivo se queda sin espacio, el
 * guardado fallaba de forma generica o silenciosa (ver
 * docs/hallazgo-almacenamiento-fotos-offline.md, recomendacion 2).
 */
async function guardarLocalConAvisoDeCuota<T>(guardar: () => Promise<T>): Promise<T> {
  try {
    return await guardar()
  } catch (err) {
    if (esErrorDeCuotaLlena(err)) throw new Error(MENSAJE_SIN_ESPACIO)
    throw err
  }
}

export {
  contarPendientes,
  listarProveedoresLocal,
  contarProductosDeProveedor,
  listarProductosLocalDeProveedor,
  obtenerUltimosErroresSync,
} from '../infrastructure/offline/db'
export {
  listarProductos,
  listarProveedores,
  completarFichaProducto,
  actualizarNombreProveedorDashboard,
  suscribirCambiosProductos,
} from '../infrastructure/supabase/repositorio-productos'

/** Foto capturada en memoria (Blob) antes de persistirla localmente. */
export interface FotoProductoEntrada {
  id: string
  etiqueta: string
  blob: Blob
}

/** Tela capturada en memoria (Blob) antes de persistirla localmente. */
export interface TelaEntrada {
  id: string
  muestrario: string
  colores: string[]
  tipo: string | null
  fotoFrenteBlob?: Blob
  fotoReversoBlob?: Blob
}

/**
 * Generamos un código TEMPORAL de proveedor (ver domain/codigo-proveedor.ts)
 * — ya no "adivinamos" el número contando lo que se ve local+remoto (ese
 * cálculo podía repetirse entre dos dispositivos y pisar datos reales al
 * sincronizar, ver docs/plan-fix-numeracion-proveedores-offline-online.md).
 * El número real y definitivo se reserva de forma atómica en el servidor
 * recién al sincronizar — ver `sincronizarProveedorReal` en sincronizador.ts.
 */
export async function generarCodigoProveedor(): Promise<string> {
  return crearCodigoProveedorTemporal(obtenerPrefijoPersona())
}

export async function siguienteNumeroProducto(proveedorCodigo: string): Promise<number> {
  return (await contarProductosDeProveedor(proveedorCodigo)) + 1
}

export interface DatosProveedor {
  codigo: string
  nombre: string
  ubicacion: string
  contacto: string
  telefono: string
  tarjetaBlob?: Blob
}

/** Guardamos local primero y sincronizamos si hay conexión. */
export async function guardarProveedor(datos: DatosProveedor): Promise<void> {
  const tarjetaBlob = datos.tarjetaBlob ? await blobAArrayBuffer(datos.tarjetaBlob) : undefined
  await guardarLocalConAvisoDeCuota(() =>
    db.proveedores.add({ ...datos, tarjetaBlob, creadoEn: Date.now(), sincronizado: 0, intentos: 0 }),
  )
  if (navigator.onLine) void sincronizarPendientes()
}

/**
 * Editar el nombre de un proveedor ya guardado (local o ya sincronizado) —
 * pedido desde la feria, 2026-09-05. Marcamos la fila pendiente de nuevo
 * (mismo patrón que actualizarProducto) para que se resincronice sola. No
 * toca fotos, así que no hay riesgo de duplicados en Storage.
 */
export async function actualizarNombreProveedor(codigo: string, nombre: string): Promise<void> {
  await guardarLocalConAvisoDeCuota(() =>
    db.proveedores.where('codigo').equals(codigo).modify({ nombre, sincronizado: 0, intentos: 0, ultimoError: undefined }),
  )
  if (navigator.onLine) void sincronizarPendientes()
}

export interface DatosProducto {
  productoId: string
  proveedorCodigo: string
  mueble: string
  categoria: string
  fob?: number
  moq?: string
  unidadesPorContenedor?: string
  colorEstructura?: string
  colorEstructuraFotoBlob?: Blob
  notas: string
  atributosEspeciales: string[]
  fotosBlobs: FotoProductoEntrada[]
  telas: TelaEntrada[]
}

/** Convertimos todas las fotos del producto (Blob) al formato que persiste Dexie (ArrayBuffer). */
async function convertirFotosProducto(datos: DatosProducto) {
  const colorEstructuraFotoBlob = datos.colorEstructuraFotoBlob
    ? await blobAArrayBuffer(datos.colorEstructuraFotoBlob)
    : undefined
  const fotosBlobs = await Promise.all(
    datos.fotosBlobs.map(async (f) => ({ ...f, blob: await blobAArrayBuffer(f.blob) })),
  )
  const telas = await Promise.all(
    datos.telas.map(async (t) => ({
      ...t,
      fotoFrenteBlob: t.fotoFrenteBlob ? await blobAArrayBuffer(t.fotoFrenteBlob) : undefined,
      fotoReversoBlob: t.fotoReversoBlob ? await blobAArrayBuffer(t.fotoReversoBlob) : undefined,
    })),
  )
  return { ...datos, colorEstructuraFotoBlob, fotosBlobs, telas }
}

/** Guardamos el producto localmente antes de cualquier intento remoto. */
export async function guardarProducto(datos: DatosProducto): Promise<void> {
  const convertido = await convertirFotosProducto(datos)
  await guardarLocalConAvisoDeCuota(() =>
    db.productos.add({ ...convertido, creadoEn: Date.now(), sincronizado: 0, intentos: 0 }),
  )
  if (navigator.onLine) void sincronizarPendientes()
}

/**
 * Actualizamos la fila local existente y la dejamos pendiente de sincronización.
 *
 * `iniciarEdicion` (captura-producto.ts) reconstruye las fotos para editarlas
 * SIN la `url` que pudiera tener cada una (solo id/blob/etiqueta — no hace
 * falta la URL para mostrarlas/editarlas) — si acá se guardara tal cual,
 * cualquier edición (aunque no toque fotos) borraría la URL ya recordada de
 * fotos con blob vacío por las 72h, reproduciendo el bug que se arregló en
 * sincronizador.ts (ver docs/hallazgo-blob-vacio-post-72h-2026-09-06.md).
 * Por eso, antes de guardar, recuperamos la fila anterior y le devolvemos su
 * `url` a cada foto que coincida por `id` — y, desde 2026-09-07, lo mismo
 * para color/estructura y cada tela (necesario ahora para saber en qué
 * backend borrar cada una, ver BackendStorage en db.ts).
 */
export async function actualizarProducto(idLocal: number, datos: DatosProducto): Promise<void> {
  const anterior = await db.productos.get(idLocal)
  const convertido = await convertirFotosProducto(datos)
  const fotosBlobs = convertido.fotosBlobs.map((foto) => {
    const previa = anterior?.fotosBlobs.find((f) => f.id === foto.id)
    return previa?.url ? { ...foto, url: previa.url } : foto
  })
  const telas = convertido.telas.map((tela) => {
    const previa = anterior?.telas.find((t) => t.id === tela.id)
    return {
      ...tela,
      ...(previa?.fotoFrenteUrl ? { fotoFrenteUrl: previa.fotoFrenteUrl } : {}),
      ...(previa?.fotoReversoUrl ? { fotoReversoUrl: previa.fotoReversoUrl } : {}),
    }
  })
  await guardarLocalConAvisoDeCuota(() =>
    db.productos.update(idLocal, {
      ...convertido,
      fotosBlobs,
      telas,
      ...(anterior?.colorEstructuraFotoUrl ? { colorEstructuraFotoUrl: anterior.colorEstructuraFotoUrl } : {}),
      sincronizado: 0,
      intentos: 0,
      ultimoError: undefined,
    }),
  )
  if (navigator.onLine) void sincronizarPendientes()
}

// ---------- Eliminar (proveedor → producto → tela, cascada) ----------
//
// Solo disponible desde mobile (pedido explícito, 2026-09-05). La fila local
// se borra YA (para que desaparezca de la lista al instante, sin pantallas de
// "eliminando…") — si el registro ya se sincronizó alguna vez (`creadoRemoto`),
// se encola un borrado real en `borradosPendientes` para que el sincronizador
// limpie Storage + Postgres en cuanto haya red (ver sincronizador.ts,
// procesarBorrados). Si nunca se sincronizó, no hay nada remoto que limpiar —
// no se encola nada.
// Ver docs/plan-eliminar-proveedor-producto-tela-2026-09-05.md.

/**
 * Rutas de un producto a borrar del storage — SOLO las que de verdad se
 * subieron alguna vez (tienen `url`/key recordada), cada una con su backend
 * real (ver `backendDeUrl`, 2026-09-07). Antes se calculaban TODAS las rutas
 * posibles de forma determinista sin saber si de verdad existían ni dónde —
 * intentar borrar "a ciegas" en el storage equivocado podía enmascarar un
 * fallo real (ver comentario en sincronizador.ts, aplicarBorradoReal).
 */
function rutasDeBorradoProducto(p: ProductoPendiente): RutaDeBorrado[] {
  const rutas: RutaDeBorrado[] = []
  const colorEstructura = backendDeUrl(p.colorEstructuraFotoUrl)
  if (colorEstructura) rutas.push(colorEstructura)
  for (const foto of p.fotosBlobs) {
    const r = backendDeUrl(foto.url)
    if (r) rutas.push(r)
  }
  for (const tela of p.telas) {
    const frente = backendDeUrl(tela.fotoFrenteUrl)
    if (frente) rutas.push(frente)
    const reverso = backendDeUrl(tela.fotoReversoUrl)
    if (reverso) rutas.push(reverso)
  }
  return rutas
}

/** Borra un único producto local (Dexie) y encola su borrado remoto si aplica. */
async function eliminarProductoInterno(p: ProductoPendiente): Promise<void> {
  if (p.creadoRemoto) {
    await db.borradosPendientes.add({
      tipo: 'producto',
      productoId: p.productoId,
      rutasStorage: rutasDeBorradoProducto(p),
      creadoEn: Date.now(),
      intentos: 0,
    })
  }
  await db.productos.delete(p.id!)
}

/** Elimina un proveedor y, en cascada, todos sus productos (y las telas de esos productos). */
export async function eliminarProveedor(codigo: string): Promise<void> {
  const productos = await listarProductosLocalDeProveedor(codigo)
  for (const p of productos) await eliminarProductoInterno(p)

  const proveedor = await db.proveedores.where('codigo').equals(codigo).first()
  if (proveedor?.creadoRemoto) {
    const tarjeta = backendDeUrl(proveedor.tarjetaUrl)
    await db.borradosPendientes.add({
      tipo: 'proveedor',
      codigoProveedor: codigo,
      rutasStorage: tarjeta ? [tarjeta] : [],
      creadoEn: Date.now(),
      intentos: 0,
    })
  }
  await db.proveedores.where('codigo').equals(codigo).delete()

  if (navigator.onLine) void sincronizarPendientes()
}

/** Elimina un producto (y en cascada las telas guardadas dentro de él). */
export async function eliminarProducto(idLocal: number): Promise<void> {
  const p = await db.productos.get(idLocal)
  if (!p) return
  await eliminarProductoInterno(p)
  if (navigator.onLine) void sincronizarPendientes()
}

/**
 * Elimina una sola tela ya guardada dentro de un producto (sin tocar el resto
 * del producto). No se puede simplemente quitarla del arreglo `telas` y
 * resincronizar el producto entero: `sincronizarProductoReal` solo hace
 * upsert de las telas presentes, nunca borra en Supabase las que
 * desaparecieron del arreglo local — dejaría la fila huérfana en Postgres
 * para siempre. Por eso esto encola un borrado explícito.
 */
export async function eliminarTela(idProductoLocal: number, telaId: string): Promise<void> {
  const p = await db.productos.get(idProductoLocal)
  if (!p) return

  if (p.creadoRemoto) {
    const tela = p.telas.find((t) => t.id === telaId)
    const rutasStorage: RutaDeBorrado[] = []
    const frente = backendDeUrl(tela?.fotoFrenteUrl)
    if (frente) rutasStorage.push(frente)
    const reverso = backendDeUrl(tela?.fotoReversoUrl)
    if (reverso) rutasStorage.push(reverso)
    await db.borradosPendientes.add({
      tipo: 'tela',
      productoId: p.productoId,
      telaId,
      rutasStorage,
      creadoEn: Date.now(),
      intentos: 0,
    })
  }
  await db.productos.update(idProductoLocal, { telas: p.telas.filter((t) => t.id !== telaId) })

  if (navigator.onLine) void sincronizarPendientes()
}

export async function sincronizarAhora() {
  return sincronizarPendientes()
}
